import { useEffect, useRef } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot, query, where, doc, getDoc } from 'firebase/firestore';
import { toMillis, mergeItemsWithServer, updatePersistentCache } from '../utils/helpers';

/**
 * Hook for Firebase real-time listeners (lists, items, user data)
 */
export default function useFirebaseListeners({
  isFirebaseAuthenticated,
  user,
  cache,
  setCache,
  updateUser,
  setAiStatusNotification,
  isOnline
}) {

  // Real-time listener for user data (including can_use_ai field)
  useEffect(() => {
    if (!isFirebaseAuthenticated || !user?.id) return;

    const userDocRef = doc(db, "users", user.id);
    const unsubscribe = onSnapshot(userDocRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const userData = docSnapshot.data();

        updateUser(currentUser => {
          if (!currentUser) return currentUser;

          const updatedUser = {
            ...currentUser,
            ...userData,
            id: currentUser.id
          };

          // Show banner when AI becomes enabled after initial load
          const hadCanUseAiField = Object.prototype.hasOwnProperty.call(currentUser, 'can_use_ai');
          if (hadCanUseAiField && currentUser.can_use_ai !== updatedUser.can_use_ai && updatedUser.can_use_ai === true) {
            setAiStatusNotification({
              type: 'success',
              message: '🎉 AI features are now available! You can use smart sorting and AI-powered list management.',
              duration: 5000
            });
          }

          return updatedUser;
        });
      }
    }, (error) => {
      if (isOnline !== false) {
        console.error("Error fetching user data:", error);
      }
    });

    return () => unsubscribe();
  }, [isFirebaseAuthenticated, user?.id, updateUser, setAiStatusNotification]);

  // Has the owned-lists listener received at least one non-empty fire?
  // Used to suppress transient empty fires when the listener first attaches
  // (those would wipe cache.lists, briefly orphan selectedList, and cause a
  // Dashboard ↔ ListDetailView flash on entry). metadata.fromCache isn't
  // reliable across SDK configurations, so we just track it ourselves.
  const ownedListsSeenRef = useRef(false);

  // Real-time listener for owned lists
  useEffect(() => {
    if (!isFirebaseAuthenticated || !user) return;

    const q = query(collection(db, "lists"), where("owner_id", "==", user.id));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const ownedLists = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        isOwner: true
      }));
      if (ownedLists.length > 0) ownedListsSeenRef.current = true;

      setCache(currentCache => {
        const existingOwned = (currentCache.lists || []).filter(l => l.isOwner && !l.tempId);
        // Suppress empty fires before we've seen any data — otherwise the
        // user's lists vanish briefly on listener attach.
        if (ownedLists.length === 0 && existingOwned.length > 0 && !ownedListsSeenRef.current) {
          return currentCache;
        }
        const sharedLists = (currentCache.lists || []).filter(list => !list.isOwner && !list.tempId);
        const allLists = [...ownedLists, ...sharedLists];
        return { ...currentCache, lists: allLists };
      });
    }, (error) => {
      if (isOnline !== false) {
        console.error("Error fetching owned lists:", error);
      }
    });

    return () => unsubscribe();
  }, [isFirebaseAuthenticated, user, setCache]);

  const sharedListsSeenRef = useRef(false);

  // Real-time listener for shared lists
  useEffect(() => {
    if (!isFirebaseAuthenticated || !user) return;

    const sharesQuery = query(
      collection(db, "list_shares"),
      where("user_id", "==", user.id),
      where("status", "==", "accepted")
    );

    const unsubscribe = onSnapshot(sharesQuery, async (querySnapshot) => {
      const sharedLists = [];

      for (const shareDoc of querySnapshot.docs) {
        const shareData = shareDoc.data();
        try {
          const listDoc = await getDoc(doc(db, "lists", shareData.list_id));
          if (listDoc.exists()) {
            const listData = listDoc.data();
            sharedLists.push({
              id: listDoc.id,
              ...listData,
              isOwner: false,
              shareInfo: {
                permission: shareData.permission,
                shared_by: shareData.shared_by,
                shared_at: shareData.created_at
              }
            });
          }
        } catch (error) {
          console.error(`Error fetching shared list ${shareData.list_id}:`, error);
        }
      }

      if (sharedLists.length > 0) sharedListsSeenRef.current = true;

      setCache(currentCache => {
        const existingShared = (currentCache.lists || []).filter(l => !l.isOwner && !l.tempId);
        // Same first-non-empty guard as the owned-lists listener.
        if (sharedLists.length === 0 && existingShared.length > 0 && !sharedListsSeenRef.current) {
          return currentCache;
        }
        const ownedLists = (currentCache.lists || []).filter(list => list.isOwner && !list.tempId);
        const allLists = [...ownedLists, ...sharedLists];
        return { ...currentCache, lists: allLists };
      });
    }, (error) => {
      if (isOnline !== false) {
        console.error("Error fetching shared lists:", error);
      }
    });

    return () => unsubscribe();
  }, [isFirebaseAuthenticated, user, setCache]);

  // Per-list flag: has this items listener received at least one non-empty
  // server fire since it attached? Reset on every (re)subscribe below.
  const itemsSeenRef = useRef({});

  // Real-time listener for items
  useEffect(() => {
    if (!isFirebaseAuthenticated || cache.lists.length === 0) return;

    // Fresh subscription: nothing has been confirmed by the server yet.
    itemsSeenRef.current = {};

    const unsubscribers = cache.lists.map(list => {
      const q = collection(db, `lists/${list.id}/items`);

      return onSnapshot(q, (querySnapshot) => {
        const serverItems = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        if (serverItems.length > 0) itemsSeenRef.current[list.id] = true;

        // Debug: Check for items with images
        const itemsWithImages = serverItems.filter(item => item.image || (item.images && item.images.primary));
        if (itemsWithImages.length > 0) {
          console.log(`🔥 [Firebase Items] Found ${itemsWithImages.length} items with images:`, itemsWithImages.map(item => ({ id: item.id, content: item.content, hasImage: !!(item.image || (item.images && item.images.primary)) })));
        }

        setCache(currentCache => {
          const currentItems = currentCache.items[list.id] || [];

          // Suppress only the *transient* empty fires. Firestore emits an empty
          // snapshot when a listener first attaches and again when it
          // re-attaches (StrictMode double-effects, dep churn that tears down
          // and re-subscribes); letting those through visibly clears
          // cache.items[listId] and produces an "appear, disappear, fade-in"
          // flicker on list entry.
          //
          // An empty snapshot is only trusted once this listener has already
          // delivered a non-empty fire — at that point the listener is
          // established and an empty result means the items really were
          // deleted (e.g. cleared from another device), so it must propagate.
          // Tracked with a ref for the same reason as ownedListsSeenRef above:
          // metadata.fromCache isn't reliable across SDK configurations.
          const isTransientEmpty = serverItems.length === 0 &&
            currentItems.length > 0 &&
            !itemsSeenRef.current[list.id];

          if (isTransientEmpty) {
            return currentCache;
          }

          const mergedItems = mergeItemsWithServer(currentItems, serverItems);

          // Simple comparison to prevent unnecessary re-renders
          const currentItemsString = JSON.stringify(currentItems.map(i => ({
            id: i.id,
            content: i.content,
            is_completed: i.is_completed,
            hasImage: !!(i.image || (i.images && i.images.primary)),
            isOptimistic: i.isOptimistic,
            updated_at: toMillis(i.updated_at || 0)
          })));
          const mergedItemsString = JSON.stringify(mergedItems.map(i => ({
            id: i.id,
            content: i.content,
            is_completed: i.is_completed,
            hasImage: !!(i.image || (i.images && i.images.primary)),
            isOptimistic: i.isOptimistic,
            updated_at: toMillis(i.updated_at || 0)
          })));

          if (currentItemsString === mergedItemsString) {
            return currentCache;
          }

          const updatedPropertyCache = updatePersistentCache(mergedItems, currentCache.propertyCache || {});

          return {
            ...currentCache,
            items: { ...currentCache.items, [list.id]: mergedItems },
            propertyCache: updatedPropertyCache
          };
        });
      }, (error) => {
        if (isOnline !== false) {
          console.error(`Error fetching items for list ${list.id}:`, error);
        }
      });
    });

    return () => unsubscribers.forEach(unsub => unsub());
  }, [isFirebaseAuthenticated, cache.lists.map(l => l.id).join(','), setCache]);
}
